import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Clock,
  Briefcase,
  CheckCircle,
  ArrowRight,
  Phone,
  Car,
} from "lucide-react";

const DriverJob = () => {
  const breadcrumbs = [
    { label: "Home", link: "/" },
    { label: "Careers", link: "/careers" },
    { label: "Driver", link: null },
  ];

  const keyResponsibilities = [
    "Provide safe, reliable transport for clients to medical appointments and health services",
    "Assist clients with social outings, shopping trips, and community activities",
    "Support clients with boarding and alighting from vehicles, including wheelchair assistance",
    "Maintain a clean, comfortable, and well-maintained vehicle",
    "Plan efficient routes and manage time effectively to ensure punctual arrivals",
    "Provide companionship and conversation during journeys",
    "Assist with carrying shopping bags and personal items as needed",
    "Complete accurate trip logs and documentation",
    "Report any vehicle maintenance issues or safety concerns promptly",
    "Ensure compliance with road rules and safe driving practices at all times",
  ];

  const essentialRequirements = [
    "Current NSW Driver's License (minimum Class C) with clean driving record",
    "Minimum 2 years of driving experience in Australia",
    "Compassionate, patient, and respectful approach to client care",
    "Excellent communication and interpersonal skills",
    "Ability to assist clients with limited mobility",
    "Reliable, punctual, and well-organized",
    "Current NDIS Worker Screening Check or willingness to obtain",
    "Current First Aid and CPR certification (or willingness to obtain)",
    "Physical capability to assist with wheelchairs and mobility aids",
    "Flexibility to work various shifts including some weekends",
  ];

  const desirableQualifications = [
    "Experience in community transport or aged care services",
    "Knowledge of the Coolamon and surrounding areas",
    "Understanding of NDIS framework and quality standards",
    "Manual handling training and experience",
    "Experience working with people with disabilities or older adults",
    "Certificate III in Individual Support or similar qualification",
    "Defensive driving course completion",
  ];

  const benefits = [
    "Competitive hourly rates with penalty rates for weekends",
    "Company vehicle provided for all client transport",
    "Fuel and vehicle maintenance costs covered",
    "Flexible casual and part-time hours available",
    "Comprehensive induction and ongoing training",
    "Supportive team environment",
    "Opportunity to make a positive difference in your community",
    "Regular work available with consistent clients",
  ];

  return (
    <div className="min-h-screen bg-white">
      <section
        className="py-12 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: "#F5F0F1" }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span>|</span>}
                {crumb.link ? (
                  <Link
                    to={crumb.link}
                    className="hover:text-[#E85D2C] transition-colors"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-gray-900">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Driver
            </h1>

            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg">
                <MapPin size={16} className="text-[#E85D2C]" />
                <span className="text-gray-700">Coolamon, NSW 2701</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg">
                <Clock size={16} className="text-[#E85D2C]" />
                <span className="text-gray-700">Casual | Part-Time</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg">
                <Briefcase size={16} className="text-[#E85D2C]" />
                <span className="text-gray-700">Support Services</span>
              </div>
              <div className="flex items-center gap-2 bg-[#E85D2C] text-white px-4 py-2 rounded-lg">
                <Car size={16} />
                <span className="font-medium">NSW License Required</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-12">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">
                  About Allawah Lodge
                </h2>
                <p className="text-base text-gray-700 font-light leading-relaxed">
                  Allawah Lodge is a trusted provider of compassionate care
                  services in Coolamon, NSW. We support older Australians and
                  people with disabilities to maintain their independence and
                  stay connected with their community. Our transport services
                  play a vital role in helping clients access essential
                  services, maintain social connections, and participate in
                  activities they enjoy.
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">
                  Role Overview
                </h2>
                <p className="text-base text-gray-700 font-light leading-relaxed">
                  We are seeking reliable, caring Drivers to provide safe
                  transport services for our clients throughout Coolamon and
                  surrounding areas. In this role, you will do more than just
                  drive – you will be a friendly face, a helping hand, and an
                  important connection to the community for people who may have
                  limited mobility or transport options.
                </p>
                <p className="text-base text-gray-700 font-light leading-relaxed">
                  This is an ideal opportunity for people who enjoy driving,
                  value independence, and want to make a genuine difference in
                  their local community. You will have the flexibility of casual
                  or part-time hours while providing an essential service that
                  enables clients to live independently and stay socially
                  connected.
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">
                  Key Responsibilities
                </h2>
                <ul className="space-y-3">
                  {keyResponsibilities.map((responsibility, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle
                        size={16}
                        className="text-[#E85D2C] flex-shrink-0 mt-1"
                      />
                      <span className="text-sm text-gray-700 font-light leading-relaxed">
                        {responsibility}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">
                  Essential Requirements
                </h2>
                <ul className="space-y-3">
                  {essentialRequirements.map((requirement, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle
                        size={16}
                        className="text-[#E85D2C] flex-shrink-0 mt-1"
                      />
                      <span className="text-sm text-gray-700 font-light leading-relaxed">
                        {requirement}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">
                  Desirable Qualifications and Experience
                </h2>
                <ul className="space-y-3">
                  {desirableQualifications.map((qualification, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle
                        size={16}
                        className="text-[#E85D2C] flex-shrink-0 mt-1"
                      />
                      <span className="text-sm text-gray-700 font-light leading-relaxed">
                        {qualification}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900">
                  What We Offer
                </h2>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle
                        size={16}
                        className="text-[#E85D2C] flex-shrink-0 mt-1"
                      />
                      <span className="text-sm text-gray-700 font-light leading-relaxed">
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8 space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  Equal Opportunity Employer
                </h2>
                <p className="text-base text-gray-700 font-light leading-relaxed">
                  Allawah Lodge is an equal opportunity employer. We welcome
                  applications from all suitably qualified candidates regardless
                  of age, gender, cultural background, disability, sexual
                  orientation, or religious beliefs. We are committed to
                  creating an inclusive workplace that reflects the diversity of
                  our community.
                </p>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-8 space-y-6">
                <div className="bg-[#E85D2C] rounded-2xl p-8 space-y-6">
                  <h3 className="text-2xl font-bold text-white">
                    Ready to Apply?
                  </h3>

                  <p className="text-base text-white/90 font-light leading-relaxed">
                    Join our team and help clients stay connected, independent,
                    and engaged in their community.
                  </p>

                  <div className="space-y-4">
                    <Link
                      to="/jobs/driver/apply"
                      className="flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-[#E85D2C] font-medium px-8 py-4 rounded-lg transition-all duration-300 w-full"
                    >
                      Apply Now
                      <ArrowRight size={18} />
                    </Link>

                    <p className="text-xs text-white/70 text-center font-light">
                      Applications are reviewed on a rolling basis
                    </p>
                  </div>

                  <div className="pt-4 border-t border-white/20 space-y-2">
                    <p className="text-sm text-white/90 font-light">
                      <strong className="font-medium">Note:</strong> Shortlisted
                      candidates will be contacted for interview. Driver license
                      verification, NDIS Worker Screening, and reference checks
                      form part of our recruitment process.
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-8 space-y-4">
                  <h3 className="text-xl font-bold text-gray-900">
                    Questions?
                  </h3>

                  <p className="text-sm text-gray-700 font-light leading-relaxed">
                    If you have any questions about this role, please contact
                    our HR team.
                  </p>

                  <a
                    href="tel:+61269272706"
                    className="inline-flex items-center gap-2 text-[#E85D2C] hover:text-[#d54d1f] font-medium transition-colors text-sm"
                  >
                    <Phone size={16} />
                    (02) 6927 2706
                  </a>

                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-[#E85D2C] hover:text-[#d54d1f] font-medium transition-colors text-sm block mt-2"
                  >
                    Contact Us
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#E85D2C] text-white py-16 lg:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
              Explore Other Career Opportunities
            </h2>

            <Link
              to="/careers"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-[#E85D2C] font-medium px-8 py-4 rounded-lg transition-all duration-300"
            >
              View All Positions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DriverJob;
